import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useInputWithDebounce } from '../../../../../../../customHook/useInputWithDebounce';
import { CompleteOperationType, StartOperationType } from '../../../../../../../model/alerts.model';
import { ClientService } from '../../../../../../../service/client.service';
import { HuddleClientService } from '../../../../../../../service/huddle-client.service';
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks';
import {
  fetchAllAccounts,
  fetchAllClients,
  saveCalendarEvent,
} from '../../../../../../../store/slices/calendarSlice';
import zeplinColor from '../../../../../../../theme/Color';
import {
  getCalendarOptions,
  timeOptions,
} from '../../../../../../../utilities/EventFormContainer.utils';
import { CalendarEvent, PlatformKey } from '../../../../../../../utilities/eventUtilies';
import { ProvisionalEvent } from '../../../CalendarGrid';
import { EventForm } from '../EventForm';

export const EventFormContainer = ({
  provisionalEvent,
  onClose,
  setProvisionalEvent,
  data,
  isEditingExisting,
  setIsEditingExisting,
  startOperation,
  completeOperation,
}: {
  provisionalEvent?: ProvisionalEvent | null;
  onClose: () => void;
  setProvisionalEvent?: React.Dispatch<React.SetStateAction<ProvisionalEvent | null>>;
  data?: CalendarEvent;
  isEditingExisting: boolean;
  setIsEditingExisting: Dispatch<SetStateAction<boolean>>;
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
}) => {
  const client = useLocation();
  const clientId = client.state?.client?.AccountId || undefined;
  const clientSource = client.state?.client?.source || '';
  const eventType = Boolean(clientId) ? 'client' : 'currentUser';
  const getInitialSelectedCalendar = () => {
    return data?.selectedCalendar || 'huddle';
  };
  const [isLoading, setIsLoading] = useState(false);

  const [selectedCalendar, setSelectedCalendar] = useState(getInitialSelectedCalendar());
  const [repeatOption, setRepeatOption] = useState<'Weekly' | 'Daily'>('Weekly');

  const [eventDate, setEventDate] = useState<Dayjs | null>(() => {
    if (isEditingExisting && data?.start) return dayjs(data.start);
    if (provisionalEvent?.date) return provisionalEvent.date;
    return dayjs();
  });
  const eventName = useInputWithDebounce(isEditingExisting && data ? data?.meetingName : '', 500);

  const clientName = useInputWithDebounce(
    isEditingExisting && data ? data.invitees?.[0]?.name || '' : client.state?.client?.Name || '',
    500,
  );
  const notes = useInputWithDebounce(
    isEditingExisting && data?.description ? data.description : '',
    500,
  );
  const dispatch = useAppDispatch();
  const { clients, accounts } = useAppSelector((state) => state.calendar);
  const calendarOptions = useMemo(() => getCalendarOptions(accounts), [accounts]);

  useEffect(() => {
    if (clients.length === 0) {
      dispatch(fetchAllClients());
    }
    if (accounts.length === 0) {
      dispatch(fetchAllAccounts());
    }
  }, [dispatch, clients.length, accounts.length]);

  if (!provisionalEvent) return null;

  const handleCreate = async () => {
    setIsLoading(true);
    if (!provisionalEvent) return;
    const platform = selectedCalendar.split(':')[0];

    const selectedClient = clients.find((c: any) => c.Name === clientName.value);
    const activeClientId = selectedClient?.AccountId || clientId;
    const activeSource = selectedClient?.source || clientSource;

    let invitees: any[] = [];
    let clientEmail = '';
    try {
      if (activeClientId) {
        const clientInfo =
          activeSource === 'Huddle'
            ? await HuddleClientService.getClientHuddleDetails(activeClientId)
            : await ClientService.getClientDetails(activeClientId);

        if (clientInfo) {
          clientEmail = clientInfo.email;
          invitees = [{ email: clientEmail, name: clientName.value, status: 'pending' }];
        }
      }

      const basePayload: any = {
        meetingName: eventName.value,
        description: notes.value,
        start: provisionalEvent.start,
        end: provisionalEvent.end,
        date: provisionalEvent.date,
        invitees,
        organizer: {
          organizerEmail: sessionStorage.getItem('current-user') || '',
          organizerName: sessionStorage.getItem('user-name') || '',
        },
      };

      if (platform === 'google') {
        basePayload.summary = eventName.value;
        if (clientEmail) {
          basePayload.attendees = [clientEmail || ''];
        }
        basePayload.recurrence = [`RRULE:FREQ=${repeatOption.toUpperCase()}`];
      } else if (platform === 'zoom') {
        basePayload.topic = eventName.value;
        basePayload.type = 8;
        basePayload.recurrence = { type: repeatOption === 'Daily' ? 1 : 2, repeat_interval: 1 };
      } else if (platform === 'microsoft') {
        basePayload.subject = eventName.value;
        basePayload.attendees = [clientName.value];
        basePayload.recurrence = {
          pattern: {
            type: repeatOption.toLowerCase(),
            interval: 1,
            daysOfWeek: [provisionalEvent.start.format('dddd').toLowerCase()],
          },
          range: { type: 'noEnd', startDate: provisionalEvent.start.format('YYYY-MM-DD') },
        };
      }

      startOperation('upload', 'Creating Event...');

      await dispatch(
        saveCalendarEvent({
          type: selectedCalendar,
          payload: basePayload,
          isEdit: false,
          clientId,
        }),
      ).unwrap();
      completeOperation('upload', 'Event Created Successfully');
      onClose();
    } catch (error) {
      completeOperation('upload', 'Error when creating event!', true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async () => {
    setIsLoading(true);
    if (!provisionalEvent || !data) return;

    const existingClient = clients.find((c: any) => c.Name === clientName.value);
    const activeClientId = existingClient?.AccountId;
    const activeSource = existingClient?.source || clientSource;

    let invitees: any[] = [];
    let clientEmail = '';
    if (activeClientId) {
      const clientInfo =
        activeSource === 'Huddle'
          ? await HuddleClientService.getClientHuddleDetails(activeClientId)
          : await ClientService.getClientDetails(activeClientId);

      if (clientInfo) {
        clientEmail = clientInfo.email;
        invitees = [{ email: clientEmail, name: clientName.value, status: 'pending' }];
      }
    }
    const platform = selectedCalendar.split(':')[0];
    const basePayload: any = {
      meetingName: eventName.value,
      description: notes.value,
      start: provisionalEvent.start,
      end: provisionalEvent.end,
      date: provisionalEvent.date,
      invitees,
      organizer: {
        organizerEmail: (data as any).organizerEmail || sessionStorage.getItem('current-user'),
        organizerName: sessionStorage.getItem('user-name') || '',
      },
    };
    if (platform === 'google') {
      basePayload.summary = eventName.value;
      if (clientEmail) {
        basePayload.attendees = [clientEmail || ''];
      }
      basePayload.recurrence = [`RRULE:FREQ=${repeatOption.toUpperCase()}`];
    } else if (platform === 'zoom') {
      basePayload.topic = eventName.value;
      basePayload.type = 8;
      basePayload.recurrence = { type: repeatOption === 'Daily' ? 1 : 2, repeat_interval: 1 };
    } else if (platform === 'microsoft') {
      basePayload.subject = eventName.value;
      basePayload.attendees = [clientName.value];
      basePayload.recurrence = {
        pattern: {
          type: repeatOption.toLowerCase(),
          interval: 1,
          daysOfWeek: [provisionalEvent.start.format('dddd').toLowerCase()],
        },
        range: { type: 'noEnd', startDate: provisionalEvent.start.format('YYYY-MM-DD') },
      };
    }

    startOperation('rename', 'Saving Changes...');
    try {
      await dispatch(
        saveCalendarEvent({
          type: selectedCalendar,
          payload: basePayload,
          isEdit: true,
          eventId: data.id,
          clientId,
        }),
      ).unwrap();
      completeOperation('rename', 'Saved Changes Successfully');
      onClose();
    } catch (error) {
      completeOperation('rename', 'Error when updating event!', true);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const calendarType = selectedCalendar.split(':')[0] as PlatformKey;

  return (
    <Box
      sx={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '380px',
        backgroundColor: zeplinColor.Neutral1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
          Create Meeting
        </Typography>
        <img
          src={'/assets/close-icon.svg'}
          alt='close'
          style={{ cursor: 'pointer', width: '24px', height: '24px' }}
          onClick={onClose}
        />
      </Box>

      <FormControl>
        <InputLabel
          id='calendar'
          sx={{
            color: zeplinColor.Neutral8,
            '&.Mui-focused': { color: zeplinColor.Brand1 },
          }}
        >
          Calendar
        </InputLabel>
        <Select
          label='Calendar'
          name='calendar'
          value={selectedCalendar}
          onChange={(e) => {
            console.log('value:$$$', e.target.value);
            setSelectedCalendar(e.target?.value);
          }}
          sx={{
            borderRadius: '12px',
            color: zeplinColor.BasicBlack,
            '& .MuiFormLabel-asterisk': {
              color: zeplinColor.Error,
            },
          }}
          IconComponent={KeyboardArrowDown}
          MenuProps={{
            PaperProps: {
              sx: {
                maxWidth: '200px',
                textOverflow: 'ellipsis',
              },
            },
          }}
        >
          {calendarOptions.map((cal: any) => (
            <MenuItem key={cal.optionName} value={cal.optionName}>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <img src={cal?.icon} alt='cal' width='20px' height='20px' />
                <Typography variant='responsiveBody2'>
                  {cal?.title} ({cal?.type?.toLocaleUpperCase()})
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <EventForm
        type={calendarType}
        eventName={eventName}
        eventDate={eventDate}
        setEventDate={setEventDate}
        initialStartTime={dayjs(provisionalEvent.start).format('h:mm a')}
        initialEndTime={dayjs(provisionalEvent.end).format('h:mm a')}
        setProvisionalEvent={setProvisionalEvent}
        timeOptions={timeOptions()}
        clients={clients || []}
        clientName={clientName}
        notes={notes}
        isEditingExisting={isEditingExisting}
        setIsEditingExisting={setIsEditingExisting}
        isLoading={isLoading}
        eventType={eventType}
        repeatOption={repeatOption}
        setRepeatOption={setRepeatOption}
        handleCreate={handleCreate}
        handleEditEvent={handleEdit}
      />
    </Box>
  );
};
